// Scope controls which pipelines consult a mute entry.
// - "spread": analyzer / bot / enricher (CEX spreads).
// - "dex_dump": dex-dump pipeline (DEX dump signals).
// Backend enforces these via `src.mute._VALID_SCOPES`.
export type MuteScope = "spread" | "dex_dump";

export const MUTE_SCOPES: readonly MuteScope[] = ["spread", "dex_dump"];

/** Resolved entity metadata attached to each mute by the dashboard
 *  backend (``/api/mutes`` and ``/api/dex-mutes``). ``null`` when the
 *  contract address (for contract mutes) or asset_key (for asset/
 *  exchange mutes) doesn't match any entity in the current
 *  mapping_human snapshot — typically an old mute for a delisted
 *  token, or a contract that was removed by ``dex_blacklist.json``.
 *  See ``src/dashboard/app.py::_build_entity_indexes`` for the
 *  resolution rules. */
export interface MuteEntity {
  asset_key: string;
  name: string;
  symbol: string;
}

export interface CexMute {
  type: "asset" | "contract" | "exchange" | "contract_exchange" | string;
  key?: string;
  chain?: string;
  contract?: string;
  exchange?: string;
  market?: string;
  until: number | string | null;
  reason?: string;
  created_at?: string;
  // Present on every fresh response — MuteEntry.to_dict() writes it
  // unconditionally. Kept optional so the frontend stays robust against
  // pre-deploy bot/analyzer responses, in-flight tests, and any audit
  // JSON that was captured before the scope migration.
  scope?: MuteScope[];
  // Resolved on the dashboard side from the current mapping snapshot.
  // ``null`` (explicit) for contract/asset mutes the resolver couldn't
  // match. Frontend uses for grouping + entity-name search.
  entity?: MuteEntity | null;
}

export interface DexMute {
  asset: string;
  chain: string;
  chain_index?: string;
  contract: string;
  price: number;
  liquidity: number;
  volume_24h: number;
  muted_at: number;
  unmute_at: number;
  ban_level: number;
  ban_hours: number;
  ob_key?: string;
  remaining_hours: number;
  // Same shape as ``CexMute.entity`` — resolved by ``/api/dex-mutes``
  // via the asset-key index (poller stamps ``asset`` at mute time) with
  // ``(chain, contract)`` fallback for renamed asset_keys.
  entity?: MuteEntity | null;
}

export interface MutesResponse {
  mutes: CexMute[];
}

export interface DexMutesResponse {
  mutes: DexMute[];
  total?: number;
}
